import React from 'react';
import { OpenFolderOutlined } from '@duxton-web/icons';
import { Button, notification, Popconfirm } from '@duxton-web/ui';
import { useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { AccessControlWrapper } from '@/common/components';
import { HTTP_STATUS, VariableState } from '@/constants';
import { useVariableContext } from '@/features/variable/contexts/variable-context';
import DeleteAndArchive from '@/features/variable/settings/archive/delete-and-archive';

import '@/features/variable/store/actions/variable/action-creators';

import { useUpdateVariable } from '@/hooks';
import { FormatErrorMessage } from '@/services/Format';
import {
  buildAccessTargetFromVariable,
  UserAction,
} from '@/utils/accessControl';
import { formatHasAccessErrorGeneric } from '@/utils/url';
import { isAdmin } from '@/utils/user';

const Archive = ({
  onDeleteAndArchiveClick = () => {},
  onDeleteAndArchiveCancel = () => {},
  onArchiveOk = () => {},
}: {
  onDeleteAndArchiveClick?: () => void;
  onDeleteAndArchiveCancel?: () => void;
  onArchiveOk?: () => void;
}) => {
  const queryClient = useQueryClient();

  const { currentVariable } = useVariableContext();

  const currentVariableIsArchived =
    currentVariable?.state === VariableState.ARCHIVED;

  const { mutate, isPending } = useUpdateVariable({
    onSuccess: () => {
      const variableName = currentVariable?.name;

      if (currentVariableIsArchived) {
        notification.success({
          message: ' Undo Archiving the Variable',
          description: `Variable ${variableName} was successfully update, you can start to to configure experiment/rollout on this variable.`,
        });
      } else {
        notification.success({
          message: 'Archiving the Variable',
          description: `Variable ${variableName} was successfully archived.`,
        });
      }

      queryClient.invalidateQueries({
        queryKey: ['planner', 'variable'],
      });

      onArchiveOk();
    },
    onError: (err: any) => {
      let description;

      if (err?.response?.status === HTTP_STATUS.FORBIDDEN)
        description = formatHasAccessErrorGeneric(err);

      if (currentVariableIsArchived) {
        if (!description)
          description = (
            <span>
              Variable was not undo archived, due to: {FormatErrorMessage(err)}
            </span>
          );

        notification.error({
          description,
          message: 'Undo Archiving variable failed',
        });
      } else {
        if (!description)
          description = (
            <span>
              Variable was not archived, due to: {FormatErrorMessage(err)}
            </span>
          );

        notification.error({
          description,
          message: 'Archiving variable failed',
        });
      }
    },
  });

  const handleArchiveVariable = () => {
    const nextVariable = produce(currentVariable, (draft) => {
      draft.state = VariableState.ARCHIVED;
    });

    mutate(nextVariable);
  };

  const handleUndoArchiveVariable = () => {
    const nextVariable = produce(currentVariable, (draft) => {
      draft.state = VariableState.INACTIVE;
    });

    mutate(nextVariable);
  };

  const renderArchiveButton = (hasAccess: boolean) =>
    hasAccess ? (
      <Popconfirm
        placement="bottomRight"
        title="Do you want to archive the variable?"
        onConfirm={handleArchiveVariable}
        okText="Confirm"
        cancelText="Cancel"
      >
        <Button
          shape="round"
          icon={<OpenFolderOutlined />}
          size="small"
          loading={isPending}
          disabled={isPending}
        >
          Archive
        </Button>
      </Popconfirm>
    ) : (
      <Button
        disabled
        shape="round"
        icon={<OpenFolderOutlined />}
        size="small"
        loading={isPending}
      >
        Archive
      </Button>
    );

  const renderUndoingArchiveButton = (hasAccess: boolean) =>
    hasAccess ? (
      <div>
        <Popconfirm
          placement="bottomRight"
          title="Do you want to undo archive the variable?"
          onConfirm={handleUndoArchiveVariable}
          okText="Confirm"
          cancelText="Cancel"
        >
          <Button
            shape="round"
            icon={<OpenFolderOutlined />}
            size="small"
            loading={isPending}
            disabled={isPending}
          >
            Undo Archive
          </Button>
        </Popconfirm>
      </div>
    ) : (
      <Button
        disabled
        shape="round"
        icon={<OpenFolderOutlined />}
        size="small"
        loading={isPending}
      >
        Undo Archive
      </Button>
    );

  if (
    ![
      VariableState.ACTIVE,
      VariableState.INACTIVE,
      VariableState.ARCHIVED,
    ].includes(currentVariable?.state as VariableState)
  )
    return null;

  if (currentVariable?.state === VariableState.ARCHIVED)
    return (
      <AccessControlWrapper
        action={UserAction.UNDO_ARCHIVE_CURRENT_VARIABLE}
        techFamilies={[currentVariable?.techFamily]}
        accessTargets={[buildAccessTargetFromVariable(currentVariable)]}
      >
        {renderUndoingArchiveButton}
      </AccessControlWrapper>
    );

  return (
    <div>
      {isAdmin() ? ( // in 1st phase, only admin can see this button. We will allow non-admin to see this button after approval flow is implemented
        <DeleteAndArchive
          onDeleteAndArchiveClick={onDeleteAndArchiveClick}
          onDeleteAndArchiveCancel={onDeleteAndArchiveCancel}
          onDeleteAndArchiveOk={onArchiveOk}
        />
      ) : (
        <AccessControlWrapper
          action={UserAction.ARCHIVE_CURRENT_VARIABLE}
          techFamilies={[currentVariable?.techFamily]}
          accessTargets={[buildAccessTargetFromVariable(currentVariable)]}
        >
          {renderArchiveButton}
        </AccessControlWrapper>
      )}
    </div>
  );
};

export default Archive;
